import { Link } from 'react-router-dom';
import { useState } from 'react';
import { 
  ArrowRight, CheckCircle, Clock, Users, Award, BookOpen, 
  Wrench, Cog, Building2, Briefcase, GraduationCap, Play,
  Monitor, FileText, Settings, Globe, Shield, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

// IFS Training Modules with detailed curriculum
const ifsModules = [
  {
    id: 'architecture',
    name: 'IFS Application Architecture',
    icon: Building2,
    duration: '1 week',
    topics: [
      'IFS Cloud Platform Overview',
      'Application Object Model',
      'Layered Architecture Understanding',
      'Navigation & User Interface',
      'Security Framework Basics'
    ]
  },
  {
    id: 'report-designer',
    name: 'IFS Report Designer Tool',
    icon: FileText,
    duration: '2 weeks',
    topics: [
      'Report Designer Interface',
      'Creating Custom Reports',
      'Data Source Configuration',
      'Report Layouts & Formatting',
      'Publishing & Distribution'
    ]
  },
  {
    id: 'solution-manager',
    name: 'IFS Solution Manager',
    icon: Settings,
    duration: '2 weeks',
    topics: [
      'Configuration Management',
      'Solution Deployment',
      'Environment Management',
      'Version Control & Migration',
      'Best Practices & Standards'
    ]
  },
  {
    id: 'eam',
    name: 'Enterprise Asset Management',
    icon: Wrench,
    duration: '3 weeks',
    topics: [
      'Asset Lifecycle Management',
      'Maintenance Planning & Scheduling',
      'Work Order Management',
      'Spare Parts & Inventory',
      'Mobile Workforce Solutions',
      'Predictive Maintenance & IoT'
    ]
  },
  {
    id: 'fsm',
    name: 'Field Service Management',
    icon: Globe,
    duration: '2 weeks',
    topics: [
      'Service Contract Management',
      'Dispatch & Scheduling',
      'Mobile Field Service',
      'Customer Portal Integration',
      'SLA Management'
    ]
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain Management',
    icon: TrendingUp,
    duration: '2 weeks',
    topics: [
      'Procurement & Sourcing',
      'Inventory Management',
      'Warehouse Operations',
      'Demand Planning',
      'Supplier Management'
    ]
  }
];

// IFS Cloud Certification Training Paths
const trainingPaths = [
  {
    id: 'financials',
    name: 'IFS Cloud Financials',
    level: 'Beginner to Advanced',
    duration: '8-10 weeks',
    description: 'Master IFS Cloud Financials to manage general ledger, accounts payable/receivable, fixed assets, cash management, and financial reporting for enterprise organizations.',
    color: 'from-emerald-500 to-emerald-700',
    icon: '💰',
    topics: [
      'General Ledger & Chart of Accounts',
      'Accounts Payable & Receivable',
      'Fixed Assets Management',
      'Cash & Treasury Management',
      'Multi-Currency & Consolidation',
      'Financial Reporting & Analysis',
      'Tax Management & Compliance',
      'Period-End Close Procedures'
    ],
    outcomes: [
      'Configure IFS Cloud Financials modules',
      'Manage end-to-end financial processes',
      'Generate financial statements & reports',
      'Handle multi-company consolidations',
      'Implement financial controls & compliance',
      'Prepare for IFS Cloud Financials certification'
    ],
    salary: '$75,000 - $115,000',
    demand: 'High',
    industries: ['All Industries', 'Financial Services', 'Manufacturing']
  },
  {
    id: 'scm',
    name: 'IFS Cloud SCM',
    level: 'Beginner to Advanced',
    duration: '10-12 weeks',
    description: 'Become an expert in IFS Cloud Supply Chain Management covering procurement, inventory, warehouse management, distribution, and supplier collaboration.',
    color: 'from-blue-500 to-blue-700',
    icon: '📦',
    topics: [
      'Procurement & Sourcing',
      'Purchase Order Management',
      'Inventory Control & Valuation',
      'Warehouse Management',
      'Distribution & Logistics',
      'Supplier Relationship Management',
      'Demand Planning & Forecasting',
      'Supply Chain Analytics'
    ],
    outcomes: [
      'Configure end-to-end supply chain processes',
      'Optimize inventory levels & costs',
      'Manage warehouse operations efficiently',
      'Implement supplier collaboration portals',
      'Generate supply chain analytics & KPIs',
      'Prepare for IFS Cloud SCM certification'
    ],
    salary: '$80,000 - $120,000',
    demand: 'Very High',
    industries: ['Manufacturing', 'Distribution', 'Retail', 'Logistics']
  },
  {
    id: 'manufacturing',
    name: 'IFS Cloud Manufacturing',
    level: 'Intermediate to Advanced',
    duration: '10-12 weeks',
    description: 'Specialize in IFS Cloud Manufacturing to manage production planning, shop floor control, quality management, and lean manufacturing for discrete and process industries.',
    color: 'from-orange-500 to-orange-700',
    icon: '🏭',
    topics: [
      'Production Planning & Scheduling',
      'Shop Floor Control & Execution',
      'Bill of Materials (BOM) Management',
      'Work Order Management',
      'Quality Management & Control',
      'Lean Manufacturing Principles',
      'Capacity Planning & MRP',
      'Manufacturing Analytics'
    ],
    outcomes: [
      'Configure manufacturing processes in IFS Cloud',
      'Manage production planning & scheduling',
      'Implement quality control procedures',
      'Optimize shop floor operations',
      'Apply lean manufacturing techniques',
      'Prepare for IFS Cloud Manufacturing certification'
    ],
    salary: '$85,000 - $125,000',
    demand: 'High',
    industries: ['Aerospace & Defense', 'Industrial Manufacturing', 'Automotive', 'Electronics']
  },
  {
    id: 'asset-management',
    name: 'IFS Cloud Asset Management',
    level: 'Intermediate to Advanced',
    duration: '8-10 weeks',
    description: 'Master IFS Cloud Enterprise Asset Management (EAM) - the flagship module for asset-intensive industries covering maintenance, reliability, and asset lifecycle management.',
    color: 'from-purple-500 to-purple-700',
    icon: '⚙️',
    topics: [
      'Asset Lifecycle Management',
      'Preventive Maintenance Planning',
      'Predictive Maintenance & IoT',
      'Work Order Management',
      'Maintenance Scheduling',
      'Spare Parts & MRO Inventory',
      'Mobile Workforce Management',
      'Reliability Engineering & MTBF'
    ],
    outcomes: [
      'Configure enterprise asset management in IFS',
      'Design maintenance strategies & schedules',
      'Implement predictive maintenance programs',
      'Manage mobile field workforce',
      'Optimize asset performance & reliability',
      'Prepare for IFS Cloud Asset Management certification'
    ],
    salary: '$85,000 - $130,000',
    demand: 'Very High',
    industries: ['Oil & Gas', 'Energy & Utilities', 'Mining', 'Aerospace & Defense']
  }
];

// Legacy paths for reference (Technical track) - can be offered as add-on
const technicalPath = {
    id: 'technical',
    name: 'IFS Technical Developer',
    level: 'Advanced',
    duration: '12 weeks',
    description: 'For developers seeking deep technical expertise in IFS customization, integrations, and extensions.',
    color: 'from-slate-600 to-slate-800',
    icon: '💻',
    topics: [
      'IFS Application Development Framework',
      'PL/SQL & IFS Foundation',
      'Custom Extensions Development',
      'IFS Connect & Integration APIs',
      'Report Designer Advanced',
      'Performance Tuning'
    ],
    outcomes: [
      'Develop custom IFS extensions',
      'Build integrations with external systems',
      'Create complex custom reports',
      'Optimize system performance',
      'Lead technical implementations'
    ],
    salary: '$90,000 - $140,000',
    demand: 'Very High'
};

// Corporate clients
const corporateClients = [
  'DHL', 'PWC', 'ATOS', 'Tech Mahindra', 'KPMG', 'Lamprell', 
  'ESSAR', 'Ashok Leyland', 'SABIC', 'TSPL', 'Neovia'
];

// Industries
const industries = [
  { name: 'Aerospace & Defense', icon: Shield },
  { name: 'Manufacturing', icon: Cog },
  { name: 'Oil & Gas', icon: Building2 },
  { name: 'Energy & Utilities', icon: TrendingUp },
  { name: 'Construction', icon: Wrench },
  { name: 'Engineering', icon: Settings }
];

export default function IFSDetailPage() {
  const [selectedPath, setSelectedPath] = useState('functional');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDemoRequest = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'IFS Demo',
        last_name: 'Request',
        email,
        message: 'Interested in IFS ERP Training - Demo Request',
        source: 'ifs_demo'
      });
      toast.success('Demo request submitted! We\'ll contact you within 24 hours.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="ifs-detail-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link to="/programs" className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 mb-6">
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Back to Programs
              </Link>
              
              <Badge className="bg-blue-100 text-blue-700 mb-4">Cloud-Based ERP Training</Badge>
              
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-3xl mb-6">
                🏭
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
                IFS Cloud ERP Training
              </h1>
              <p className="text-xl text-blue-600 font-medium mb-4">
                Master Enterprise Asset Management & Service Excellence
              </p>
              <p className="text-lg text-slate-600 mb-8">
                IFS ERP is a cloud-based Enterprise Resource Planning solution that integrates data and processes 
                across multiple departments. Our comprehensive training covers IFS Application architecture, 
                Report Designer, Solution Manager, and real-time scenario-based implementations from basic to advanced levels.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/pricing">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6" data-testid="ifs-enroll-btn">
                    Enroll Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/webinar">
                  <Button variant="outline" className="rounded-full px-6 border-blue-200 text-blue-700 hover:bg-blue-50">
                    <Play className="w-4 h-4 mr-2" /> Free Demo Session
                  </Button>
                </Link>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">1000+</p>
                  <p className="text-xs text-slate-500">Trained Professionals</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">95%</p>
                  <p className="text-xs text-slate-500">Placement Rate</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                  <p className="text-2xl font-bold text-blue-600">50+</p>
                  <p className="text-xs text-slate-500">Corporate Clients</p>
                </div>
              </div>
            </div>
            
            {/* Program Highlights Card */}
            <div>
              <Card className="shadow-xl border-0 bg-white">
                <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
                  <CardTitle className="flex items-center">
                    <Award className="w-5 h-5 mr-2" />
                    Program Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Flexible Duration</p>
                      <p className="text-sm text-slate-600">6-12 weeks based on track</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Monitor className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Live Online Training</p>
                      <p className="text-sm text-slate-600">Expert-led via Zoom/Teams</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Hands-On Labs</p>
                      <p className="text-sm text-slate-600">Real IFS sandbox access</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Certification Prep</p>
                      <p className="text-sm text-slate-600">IFS certification guidance</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Job Assistance</p>
                      <p className="text-sm text-slate-600">Resume & interview prep</p>
                    </div>
                  </div>
                  
                  {/* Demo Request Form */}
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-sm font-medium text-slate-700 mb-2">Request Free Demo</p>
                    <form onSubmit={handleDemoRequest} className="flex gap-2">
                      <Input 
                        type="email" 
                        placeholder="Enter your email" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1"
                        data-testid="ifs-demo-email"
                      />
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={loading}>
                        {loading ? '...' : 'Go'}
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Industries Using IFS Cloud</p>
          <div className="flex flex-wrap justify-center gap-4">
            {industries.map((industry) => (
              <div key={industry.name} className="flex items-center px-4 py-2 bg-slate-50 rounded-full text-sm">
                <industry.icon className="w-4 h-4 text-blue-600 mr-2" />
                <span className="text-slate-700">{industry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IFS Cloud Certification Paths */}
      <section className="py-20 lg:py-28" id="certifications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Official IFS Cloud Certifications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Choose Your IFS Cloud Certification Path</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Enroll in specific IFS Cloud certification training aligned with your career goals and industry focus
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {trainingPaths.map((path) => (
              <Card 
                key={path.id} 
                className={`relative overflow-hidden border-2 transition-all duration-300 cursor-pointer ${
                  selectedPath === path.id ? 'border-blue-500 shadow-xl ring-2 ring-blue-200' : 'border-slate-200 hover:border-blue-300 hover:shadow-lg'
                }`}
                onClick={() => setSelectedPath(path.id)}
                data-testid={`cert-path-${path.id}`}
              >
                <div className={`h-2 bg-gradient-to-r ${path.color}`} />
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{path.icon}</span>
                      <div>
                        <CardTitle className="text-xl text-slate-900">{path.name}</CardTitle>
                        <p className="text-sm text-slate-500">{path.level} • {path.duration}</p>
                      </div>
                    </div>
                    <Badge className={`${path.demand === 'Very High' ? 'bg-green-500' : 'bg-green-100 text-green-700'} ${path.demand === 'Very High' ? 'text-white' : ''}`}>
                      {path.demand} Demand
                    </Badge>
                  </div>
                  <CardDescription className="text-slate-600">{path.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Industries */}
                    {path.industries && (
                      <div className="flex flex-wrap gap-2">
                        {path.industries.map((ind, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">{ind}</span>
                        ))}
                      </div>
                    )}
                    
                    {/* Salary */}
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-slate-600">Expected Salary:</span>
                      <span className="font-bold text-green-700">{path.salary}</span>
                    </div>
                    
                    {/* Topics */}
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics:</p>
                      <div className="grid grid-cols-2 gap-1">
                        {path.topics.slice(0, 6).map((topic, i) => (
                          <div key={i} className="flex items-center text-xs text-slate-600">
                            <CheckCircle className="w-3 h-3 text-blue-500 mr-1 flex-shrink-0" />
                            <span className="truncate">{topic}</span>
                          </div>
                        ))}
                      </div>
                      {path.topics.length > 6 && (
                        <p className="text-xs text-blue-600 mt-1">+{path.topics.length - 6} more topics</p>
                      )}
                    </div>
                    
                    {/* CTA */}
                    <div className="flex gap-3 pt-2">
                      <Link to="/pricing" className="flex-1">
                        <Button className={`w-full bg-gradient-to-r ${path.color} text-white hover:opacity-90`}>
                          Enroll Now
                        </Button>
                      </Link>
                      <Link to="/webinar">
                        <Button variant="outline" className="border-slate-300">
                          <Play className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Technical Developer Add-on */}
          <div className="mt-12 p-6 bg-slate-100 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-3xl">{technicalPath.icon}</span>
                <div>
                  <h3 className="font-bold text-slate-900">{technicalPath.name} <Badge variant="outline" className="ml-2">Add-on Track</Badge></h3>
                  <p className="text-sm text-slate-600">For developers: Combine with any certification path • {technicalPath.duration} • {technicalPath.salary}</p>
                </div>
              </div>
              <Link to="/contact">
                <Button variant="outline" className="border-slate-400">
                  Inquire About Technical Track
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Curriculum */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">IFS Training Curriculum</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive modules covering all essential IFS Cloud functionalities
            </p>
          </div>
          
          <Tabs defaultValue="architecture" className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {ifsModules.slice(0, 4).map((module) => (
                <TabsTrigger 
                  key={module.id} 
                  value={module.id}
                  className="px-4 py-2 rounded-full border border-slate-200 data-[state=active]:border-blue-500 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700"
                >
                  {module.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {ifsModules.map((module) => (
              <TabsContent key={module.id} value={module.id}>
                <Card className="max-w-4xl mx-auto">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <module.icon className="w-6 h-6 text-blue-700" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900">{module.name}</h3>
                        <p className="text-blue-600 font-medium">Duration: {module.duration}</p>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">Topics Covered</h4>
                        <ul className="space-y-3">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-4">What You'll Learn</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start space-x-2">
                            <BookOpen className="w-5 h-5 text-blue-500 mt-0.5" />
                            <span className="text-slate-600">Real-time scenario-based training</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Monitor className="w-5 h-5 text-blue-500 mt-0.5" />
                            <span className="text-slate-600">Hands-on lab exercises</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
                            <span className="text-slate-600">Study materials & PPTs</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Award className="w-5 h-5 text-blue-500 mt-0.5" />
                            <span className="text-slate-600">Certification guidance</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

          {/* All Modules Grid */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">All IFS Modules</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {ifsModules.map((module, index) => (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-blue-700 font-mono font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{module.name}</p>
                        <p className="text-xs text-slate-500">{module.duration}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Outcomes */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">IFS Cloud Certifications We Prepare You For</h2>
              <p className="text-slate-600 mb-6">
                Our training programs are specifically designed to prepare you for official IFS Cloud certifications.
              </p>
              <ul className="space-y-3">
                {[
                  { cert: 'IFS Cloud Financials Certification', color: 'text-emerald-600' },
                  { cert: 'IFS Cloud SCM Certification', color: 'text-blue-600' },
                  { cert: 'IFS Cloud Manufacturing Certification', color: 'text-orange-600' },
                  { cert: 'IFS Cloud Asset Management Certification', color: 'text-purple-600' },
                  { cert: 'IFS Technical Developer Certification', color: 'text-slate-600' }
                ].map((item, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Award className={`w-5 h-5 ${item.color} mt-0.5`} />
                    <span className="text-slate-700 font-medium">{item.cert}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Career Opportunities</h2>
              <p className="text-slate-600 mb-6">
                IFS Cloud skills are in high demand for asset-intensive industries. 
                Here are roles you can pursue after certification.
              </p>
              <ul className="space-y-3">
                {[
                  'IFS Cloud Financials Consultant',
                  'IFS Supply Chain Consultant',
                  'IFS Manufacturing Specialist',
                  'IFS Asset Management Consultant',
                  'IFS Solution Architect',
                  'IFS Project Manager',
                  'IFS Integration Specialist',
                  'IFS Support Analyst'
                ].map((role, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <Briefcase className="w-5 h-5 text-blue-500 mt-0.5" />
                    <span className="text-slate-700">{role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Clients */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 mb-6 font-medium">Trusted by Leading Organizations</p>
          <div className="flex flex-wrap justify-center gap-6">
            {corporateClients.map((client) => (
              <div key={client} className="px-6 py-3 bg-white rounded-lg shadow-sm text-slate-700 font-medium">
                {client}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Benefits */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Why Choose Our IFS Training?</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Expert Instructors', desc: 'Learn from IFS-certified consultants with real project experience' },
              { icon: Monitor, title: 'Live Online Sessions', desc: 'Interactive training via Zoom/Teams with screen sharing & Q&A' },
              { icon: Wrench, title: 'Hands-On Labs', desc: 'Practice on real IFS sandbox environment throughout training' },
              { icon: FileText, title: 'Study Materials', desc: 'Comprehensive PPTs, documentation, and practice exercises' },
              { icon: Award, title: 'Certification Prep', desc: 'Guidance for IFS certification exams with practice tests' },
              { icon: Briefcase, title: 'Career Support', desc: 'Resume building, mock interviews, and job placement assistance' }
            ].map((benefit, i) => (
              <Card key={i} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-blue-700" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Master IFS Cloud ERP?
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Join our next batch and start your journey to becoming a certified IFS professional. 
              Customized training available for individuals and corporate teams.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="bg-white text-blue-700 hover:bg-blue-50 rounded-full px-8 py-3 font-medium">
                  View Pricing & Enroll
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-medium">
                  Request Corporate Training
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
